import React, { useEffect, useRef, useState } from 'react';
import { resumeData } from '../data/resumeData';

const Experience = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const timelineContainerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !timelineRef.current || !timelineContainerRef.current) return;

      const section = sectionRef.current;
      const timelineContainer = timelineContainerRef.current;
      const sectionTop = section.offsetTop;
      const timelineHeight = timelineContainer.offsetHeight;
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;

      // Start when the section title is in view
      const startPoint = sectionTop - windowHeight + 400;
      // End when we reach the bottom of the timeline container
      const endPoint = sectionTop + timelineHeight - windowHeight / 2;

      if (scrollY < startPoint) {
        setScrollProgress(0);
      } else if (scrollY >= endPoint) {
        setScrollProgress(100);
      } else {
        const scrollRange = endPoint - startPoint;
        const scrollPosition = scrollY - startPoint;
        const progress = (scrollPosition / scrollRange) * 100;
        setScrollProgress(Math.min(Math.max(progress, 0), 100));
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section ref={sectionRef} id="experience" className="py-20 px-4 bg-gray-50 dark:bg-gray-800/50">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" style={{ lineHeight: 1.4 }}>
          Experience
        </h2>
        <div ref={timelineContainerRef} className="relative">
          {/* Timeline background line - full length, light color */}
          <div className="hidden md:block absolute left-1/4 top-0 bottom-0 w-0.5 bg-gray-300 dark:bg-gray-600"></div>

          {/* Animated timeline line - grows with scroll */}
          <div
            ref={timelineRef}
            className="hidden md:block absolute left-1/4 top-0 w-0.5 bg-gradient-to-b from-blue-600 via-purple-600 to-pink-600 transition-all duration-300 ease-out"
            style={{ height: `${scrollProgress}%` }}
          ></div>

          {resumeData.experience.map((exp, index) => (
            <div key={index} className="relative mb-12 md:grid md:grid-cols-12 md:gap-8">
              {/* Date on the left side of timeline - hidden on mobile */}
              <div className="hidden md:flex md:col-span-3 justify-end items-start pt-6">
                <div className="text-right">
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{exp.duration}</p>
                  <p className="text-base text-gray-600 dark:text-gray-400 mt-1">{exp.location}</p>
                  <p className="mt-3 inline-block px-4 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-semibold">
                    {exp.role}
                  </p>
                </div>
              </div>

              {/* Timeline dot */}
              <div className="hidden md:flex md:col-span-0 absolute left-1/4 top-6 -ml-2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white dark:border-gray-900 shadow-lg z-10"></div>

              {/* Content card on the right */}
              <div className="md:col-span-9 md:ml-8 bg-white dark:bg-gray-900 rounded-xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 dark:border-gray-700">
                <div className="mb-4">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                    {exp.company}
                  </h3>
                  {/*<p className="text-lg text-blue-600 dark:text-blue-400 font-semibold">*/}
                  {/*  {exp.project}*/}
                  {/*</p>*/}
                  {/* Show dates on mobile */}
                  <div className="md:hidden mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <p className="font-semibold">{exp.duration}</p>
                    <p>{exp.location}</p>
                    <p className="mt-1 inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs">
                      {exp.role}
                    </p>
                  </div>
                </div>

                {exp.description && (
                  <p className="text-gray-600 dark:text-gray-400 mb-4 italic">
                    {exp.description}
                  </p>
                )}

                <ul className="space-y-3">
                  {exp.points.map((point, idx) => (
                    <li key={idx} className="flex items-start group">
                      <span className="inline-block w-2 h-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mr-3 mt-2 flex-shrink-0 group-hover:scale-150 transition-transform"></span>
                      <span className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;

